import { Quaternion, Vector3 } from "three";
import type { PlacementType } from "../../api/types/Product";
import {
  flattenPathAdaptive,
  pathNormalAt,
  pathPointAt,
  projectPointOnPath,
} from "../../shape/segment/axis/pathHelpers";
import type { SegmentSpec, XzPoint } from "../../shape/segment/types";

export type AnchorSide = 1 | -1;

/** Segment path üzerindeki tüm kalıcı anchor'ların ortak tabanı. */
export interface SegmentPathAnchor {
  kind: "wall";
  segmentId: string;
  /** Path boyunca normalize arc-length [0, 1]. */
  s: number;
  side: AnchorSide;
  /** Centerline'dan anchor noktasına doğru pozitif mesafe. */
  normalOffset: number;
}

export type ProductPlacementMode = "floor-only" | "floor-and-wall";
export type ProductFloorAnchor = {
  kind: "floor";
  x: number;
  z: number;
  y: number;
};
export type ProductWallAnchor = SegmentPathAnchor & { height: number };
export type ProductSurfaceAnchor = ProductFloorAnchor | ProductWallAnchor;
export type ProductLocalAnchor = {
  kind: "product";
  modelUuid: string;
  local: XzPoint;
};

type AnchorHost = { userData: Record<string, any> };

export function productAnchorsOf(host: AnchorHost): ProductSurfaceAnchor[] {
  const stored = host.userData.surfaceAnchors;
  const anchors = Array.isArray(stored)
    ? stored
    : host.userData.surfaceAnchor
      ? [host.userData.surfaceAnchor]
      : [];
  return anchors.map((anchor) => ({ ...anchor })) as ProductSurfaceAnchor[];
}

export function setProductAnchors(
  host: AnchorHost,
  anchors: readonly ProductSurfaceAnchor[],
): void {
  if (anchors.length) {
    host.userData.surfaceAnchors = anchors.map((anchor) => ({ ...anchor }));
  } else {
    delete host.userData.surfaceAnchors;
  }
  delete host.userData.surfaceAnchor;
}

const WALL_TYPES = new Set<PlacementType>([
  "WALL",
  "WALLANDFLOORANDOBJECT",
  "WALLANDFLOORANDCEIL",
  "FLOORANDWALL",
]);

export function productPlacementMode(
  placeType?: PlacementType | null,
): ProductPlacementMode {
  return placeType && WALL_TYPES.has(placeType)
    ? "floor-and-wall"
    : "floor-only";
}

function arcMetrics(path: SegmentSpec["path"]) {
  const samples = flattenPathAdaptive(path);
  const cumulative = [0];
  for (let i = 1; i < samples.length; i++) {
    const a = samples[i - 1]!.point;
    const b = samples[i]!.point;
    cumulative.push(cumulative[i - 1]! + Math.hypot(b.x - a.x, b.z - a.z));
  }
  return { samples, cumulative, length: cumulative.at(-1) ?? 0 };
}

export function normalizedArcAtT(spec: SegmentSpec, t: number): number {
  const metrics = arcMetrics(spec.path);
  if (metrics.length <= 1e-9) return 0;
  for (let i = 1; i < metrics.samples.length; i++) {
    const a = metrics.samples[i - 1]!;
    const b = metrics.samples[i]!;
    if (t > b.t) continue;
    const alpha = (t - a.t) / (b.t - a.t || 1);
    return (
      metrics.cumulative[i - 1]! +
      (metrics.cumulative[i]! - metrics.cumulative[i - 1]!) * alpha
    ) / metrics.length;
  }
  return 1;
}

export function tAtNormalizedArc(spec: SegmentSpec, s: number): number {
  const metrics = arcMetrics(spec.path);
  const distance = Math.max(0, Math.min(1, s)) * metrics.length;
  for (let i = 1; i < metrics.cumulative.length; i++) {
    if (distance > metrics.cumulative[i]!) continue;
    const start = metrics.cumulative[i - 1]!;
    const alpha = (distance - start) / (metrics.cumulative[i]! - start || 1);
    const a = metrics.samples[i - 1]!;
    const b = metrics.samples[i]!;
    return a.t + (b.t - a.t) * alpha;
  }
  return 1;
}

export function segmentPathAnchorAtT(
  spec: SegmentSpec,
  t: number,
  side: AnchorSide,
  normalOffset: number,
): SegmentPathAnchor {
  return {
    kind: "wall",
    segmentId: spec.id,
    s: normalizedArcAtT(spec, t),
    side,
    normalOffset,
  };
}

export function segmentPathAnchorFromPoint(
  spec: SegmentSpec,
  point: XzPoint,
  side: AnchorSide,
  normalOffset: number,
): SegmentPathAnchor {
  return segmentPathAnchorAtT(
    spec,
    projectPointOnPath(spec.path, point).t,
    side,
    normalOffset,
  );
}

export function resolveSegmentPathAnchor(
  spec: SegmentSpec,
  anchor: SegmentPathAnchor,
): { point: XzPoint; normal: XzPoint; t: number } {
  const t = tAtNormalizedArc(spec, anchor.s);
  const center = pathPointAt(spec.path, t);
  const pathNormal = pathNormalAt(spec.path, t);
  // Eski measurement kayıtları logical edge (1 | 3) tutuyordu.
  const storedSide = anchor.side as number;
  const side: AnchorSide = storedSide === 3 ? -1 : anchor.side;
  const normalOffset = Number.isFinite(anchor.normalOffset)
    ? anchor.normalOffset
    : storedSide === 3
      ? spec.downThickness
      : spec.upThickness;
  const normal = {
    x: pathNormal.x * side,
    z: pathNormal.z * side,
  };
  return {
    t,
    normal,
    point: {
      x: center.x + normal.x * normalOffset,
      z: center.z + normal.z * normalOffset,
    },
  };
}

export function productWallAnchorFromPoint(
  spec: SegmentSpec,
  point: XzPoint,
  height: number,
  side: AnchorSide,
  normalOffset = 0,
): ProductWallAnchor {
  return {
    ...segmentPathAnchorFromPoint(spec, point, side, normalOffset),
    height,
  };
}

export function productWallAnchorTransform(
  spec: SegmentSpec,
  anchor: ProductWallAnchor,
): { position: Vector3; quaternion: Quaternion } {
  const resolved = resolveSegmentPathAnchor(spec, anchor);
  return {
    position: new Vector3(
      resolved.point.x,
      anchor.height,
      resolved.point.z,
    ),
    quaternion: new Quaternion().setFromUnitVectors(
      new Vector3(0, 0, 1),
      new Vector3(resolved.normal.x, 0, resolved.normal.z).normalize(),
    ),
  };
}

export function productWallAnchorsTransform(
  specs: readonly SegmentSpec[],
  anchors: readonly ProductWallAnchor[],
): { position: Vector3; quaternion: Quaternion } | undefined {
  const primarySpec = specs[0];
  const primary = anchors[0];
  if (!primarySpec || !primary) return undefined;
  const primaryTransform = productWallAnchorTransform(primarySpec, primary);
  const secondarySpec = specs[1];
  const secondary = anchors[1];
  if (!secondarySpec || !secondary) return primaryTransform;

  const a = productWallAnchorTransform(primarySpec, primary);
  const b = productWallAnchorTransform(secondarySpec, secondary);
  const an = new Vector3(0, 0, 1)
    .applyQuaternion(a.quaternion)
    .setY(0)
    .normalize();
  const bn = new Vector3(0, 0, 1)
    .applyQuaternion(b.quaternion)
    .setY(0)
    .normalize();
  const determinant = an.x * bn.z - an.z * bn.x;
  if (Math.abs(determinant) < 1e-4) return primaryTransform;

  const ac = an.x * a.position.x + an.z * a.position.z;
  const bc = bn.x * b.position.x + bn.z * b.position.z;
  primaryTransform.position.set(
    (ac * bn.z - an.z * bc) / determinant,
    primary.height,
    (an.x * bc - ac * bn.x) / determinant,
  );
  return primaryTransform;
}
